'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import TodoItem from './TodoItem';
import TodoForm from './TodoForm';
import { useAuth } from '@/lib/auth-context';

interface Todo {
  id: string;
  title: string;
  details: string | null;
  notes: string | null;
  due_date: string | null;
  delegate: string | null;
  sort_order: number;
  deleted_at: string | null;
  archived_at: string | null;
  decision_id: string | null;
  suggestion_reason: string | null;
  confidence: number | null;
  sensitive: boolean;
}

interface TodoListProps {
  initialTodos: Todo[];
  compact?: boolean;
  limit?: number;
  showAddForm?: boolean;
  onTodoClick?: (todo: Todo) => void;
  initialEditId?: string | null;
  onTodoChange?: () => void;
}

export default function TodoList({
  initialTodos,
  compact = false,
  limit,
  showAddForm = true,
  onTodoClick,
  initialEditId,
  onTodoChange,
}: TodoListProps) {
  const router = useRouter();
  const { permissions, isLoading: authLoading } = useAuth();
  const [todos, setTodos] = useState<Todo[]>(initialTodos);
  const [showForm, setShowForm] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(() => {
    if (initialEditId) {
      return initialTodos.find(t => t.id === initialEditId) || null;
    }
    return null;
  });

  // Prevent hydration mismatch with dnd-kit's auto-generated IDs
  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleTodoClick = (todo: Todo) => {
    if (onTodoClick) {
      onTodoClick(todo);
    } else if (compact) {
      router.push(`/todos?edit=${todo.id}`);
    } else {
      // In non-compact mode, open the editor directly
      handleEdit(todo);
    }
  };

  useEffect(() => {
    setTodos(initialTodos);
  }, [initialTodos]);

  // Handle initialEditId - open todo for editing when todos load
  useEffect(() => {
    if (initialEditId && initialTodos.length > 0) {
      const todoToEdit = initialTodos.find(t => t.id === initialEditId);
      if (todoToEdit) {
        setEditingTodo(todoToEdit);
      }
    }
  }, [initialEditId, initialTodos]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activeTodos = todos.filter(t => !t.deleted_at);
  const deletedTodos = todos.filter(t => t.deleted_at);
  const displayTodos = limit ? activeTodos.slice(0, limit) : activeTodos;

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = activeTodos.findIndex(t => t.id === active.id);
      const newIndex = activeTodos.findIndex(t => t.id === over.id);

      const newOrder = arrayMove(activeTodos, oldIndex, newIndex);
      setTodos([...newOrder, ...deletedTodos]);

      // Persist new order
      try {
        await fetch('/api/todos/reorder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: newOrder.map(t => t.id) }),
        });
      } catch (error) {
        console.error('Failed to save order:', error);
        // Revert on error
        setTodos(initialTodos);
      }
    }
  };

  const handleDelete = async (id: string) => {
    // Optimistic update
    setTodos(prev =>
      prev.map(t =>
        t.id === id ? { ...t, deleted_at: new Date().toISOString() } : t
      )
    );

    try {
      const response = await fetch(`/api/todos/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete');
    } catch (error) {
      console.error('Failed to delete todo:', error);
      // Revert on error
      setTodos(prev =>
        prev.map(t => (t.id === id ? { ...t, deleted_at: null } : t))
      );
    }
  };

  const handleRestore = async (id: string) => {
    // Optimistic update
    setTodos(prev =>
      prev.map(t =>
        t.id === id ? { ...t, deleted_at: null, archived_at: null } : t
      )
    );

    try {
      const response = await fetch(`/api/todos/${id}/restore`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to restore');
    } catch (error) {
      console.error('Failed to restore todo:', error);
      setTodos(initialTodos);
    }
  };

  const handleAdd = async (data: { title: string; details?: string; due_date?: string; delegate?: string }) => {
    try {
      const response = await fetch('/api/todos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const { todo } = await response.json();
      setTodos(prev => [...prev, todo]);
      setShowForm(false);
      onTodoChange?.();
    } catch (error) {
      console.error('Failed to create todo:', error);
    }
  };

  const handleEdit = (todo: Todo) => {
    setEditingTodo(todo);
    setShowForm(false);
  };

  const handleUpdate = async (data: { title: string; details?: string; due_date?: string; delegate?: string; sensitive?: boolean }) => {
    if (!editingTodo) return;

    // Optimistic update
    const updatedTodo = {
      ...editingTodo,
      title: data.title,
      details: data.details || null,
      due_date: data.due_date || null,
      delegate: data.delegate || null,
      sensitive: data.sensitive !== undefined ? data.sensitive : editingTodo.sensitive,
    };
    setTodos(prev => prev.map(t => (t.id === editingTodo.id ? updatedTodo : t)));
    setEditingTodo(null);

    try {
      const response = await fetch(`/api/todos/${editingTodo.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error('Failed to update');

      const { todo } = await response.json();
      setTodos(prev => prev.map(t => (t.id === todo.id ? todo : t)));
    } catch (error) {
      console.error('Failed to update todo:', error);
      // Revert on error
      setTodos(prev => prev.map(t => (t.id === editingTodo.id ? editingTodo : t)));
    }
  };

  const handlePurge = async (id: string) => {
    const todoToRemove = todos.find(t => t.id === id);
    const title = todoToRemove?.title || 'this todo';

    if (!confirm(`Permanently delete "${title}"? This cannot be undone.`)) {
      return;
    }

    // Optimistic update - remove from list
    setTodos(prev => prev.filter(t => t.id !== id));

    try {
      const response = await fetch(`/api/todos/${id}/purge`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to purge');
    } catch (error) {
      console.error('Failed to purge todo:', error);
      // Revert on error
      if (todoToRemove) {
        setTodos(prev => [...prev, todoToRemove]);
      }
    }
  };

  const handleMoveToTop = async (id: string) => {
    const todoIndex = activeTodos.findIndex(t => t.id === id);
    if (todoIndex <= 0) return; // Already at top or not found

    // Move to top
    const newOrder = [
      activeTodos[todoIndex],
      ...activeTodos.slice(0, todoIndex),
      ...activeTodos.slice(todoIndex + 1),
    ];
    setTodos([...newOrder, ...deletedTodos]);

    // Persist new order
    try {
      await fetch('/api/todos/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: newOrder.map(t => t.id) }),
      });
    } catch (error) {
      console.error('Failed to save order:', error);
      // Revert on error
      setTodos(initialTodos);
    }
  };

  const handleUpdateNotes = async (id: string, notes: string) => {
    // Optimistic update
    setTodos(prev =>
      prev.map(t => (t.id === id ? { ...t, notes: notes || null } : t))
    );

    try {
      const response = await fetch(`/api/todos/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes || null }),
      });

      if (!response.ok) throw new Error('Failed to update notes');

      const { todo } = await response.json();
      setTodos(prev => prev.map(t => (t.id === todo.id ? todo : t)));
    } catch (error) {
      console.error('Failed to update notes:', error);
      // Revert on error
      setTodos(initialTodos);
    }
  };

  if (todos.length === 0 && !showForm) {
    return (
      <div className="text-center py-4">
        <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-3">No todos yet.</p>
        {showAddForm && !authLoading && permissions.canWrite && (
          <button
            onClick={() => setShowForm(true)}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
          >
            + Add your first todo
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Add button/form - at top (only if user can write) */}
      {showAddForm && !authLoading && permissions.canWrite && (
        <div className="pb-2">
          {showForm ? (
            <TodoForm
              onSubmit={handleAdd}
              onCancel={() => setShowForm(false)}
              compact={compact}
            />
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="w-full py-2 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 border border-dashed border-zinc-300 dark:border-zinc-700 rounded-lg hover:border-zinc-400 dark:hover:border-zinc-600 transition-colors"
            >
              + Add todo
            </button>
          )}
        </div>
      )}

      {isClient ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={displayTodos.map(t => t.id)}
            strategy={verticalListSortingStrategy}
          >
            {displayTodos.map(todo => (
              editingTodo?.id === todo.id ? (
                <div key={todo.id} className="bg-white dark:bg-zinc-900 rounded-lg border border-blue-300 dark:border-blue-700 p-3">
                  <TodoForm
                    key={`edit-${todo.id}`}
                    onSubmit={handleUpdate}
                    onCancel={() => setEditingTodo(null)}
                    initialData={todo}
                    decisionId={todo.decision_id}
                    readOnly={!permissions.canWrite}
                  />
                </div>
              ) : (
                <TodoItem
                  key={todo.id}
                  todo={todo}
                  onDelete={permissions.canWrite ? handleDelete : () => {}}
                  onRestore={permissions.canWrite ? handleRestore : undefined}
                  onMoveToTop={!compact && permissions.canWrite ? handleMoveToTop : undefined}
                  onUpdateNotes={!compact ? handleUpdateNotes : undefined}
                  onClick={handleTodoClick}
                  compact={compact}
                  showDragHandle={permissions.canWrite}
                  showActions={permissions.canWrite}
                  showAssist={permissions.canReflect}
                />
              )
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        // Server-side render without dnd-kit to prevent hydration mismatch
        <div className="space-y-2">
          {displayTodos.map(todo => (
            editingTodo?.id === todo.id ? (
              <div key={todo.id} className="bg-white dark:bg-zinc-900 rounded-lg border border-blue-300 dark:border-blue-700 p-3">
                <TodoForm
                  key={`edit-${todo.id}`}
                  onSubmit={handleUpdate}
                  onCancel={() => setEditingTodo(null)}
                  initialData={todo}
                  decisionId={todo.decision_id}
                  readOnly={!permissions.canWrite}
                />
              </div>
            ) : (
              <TodoItem
                key={todo.id}
                todo={todo}
                onDelete={permissions.canWrite ? handleDelete : () => {}}
                onRestore={permissions.canWrite ? handleRestore : undefined}
                onMoveToTop={!compact && permissions.canWrite ? handleMoveToTop : undefined}
                onUpdateNotes={!compact ? handleUpdateNotes : undefined}
                onClick={handleTodoClick}
                compact={compact}
                showDragHandle={false}
                showActions={permissions.canWrite}
                showAssist={permissions.canReflect}
              />
            )
          ))}
        </div>
      )}

      {/* Show deleted todos (crossed out) */}
      {deletedTodos.length > 0 && !compact && (
        <div className="pt-2 space-y-2">
          {deletedTodos.map(todo => (
            <TodoItem
              key={todo.id}
              todo={todo}
              onDelete={permissions.canWrite ? handleDelete : () => {}}
              onRestore={permissions.canWrite ? handleRestore : undefined}
              onPurge={permissions.canPurge ? handlePurge : undefined}
              compact={compact}
              showDragHandle={false}
              showActions={permissions.canWrite}
            />
          ))}
        </div>
      )}

      {/* More todos indicator */}
      {limit && activeTodos.length > limit && (
        <p className="text-xs text-zinc-400 dark:text-zinc-500 text-center pt-1">
          +{activeTodos.length - limit} more
        </p>
      )}
    </div>
  );
}
